import { useState, useEffect } from 'react'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { fetchCustomerStats } from '@/supabase-client'

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP'
  }).format(amount)
}

export function CustomerStatsSection({ customerId }) {
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const loadStats = async () => {
      if (!customerId) return

      try {
        setLoading(true)
        const data = await fetchCustomerStats(customerId)
        setStats(data)
      } catch (error) {
        console.error('Error loading customer stats:', error)
      } finally {
        setLoading(false)
      }
    }

    loadStats()
  }, [customerId])

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Statistics</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="space-y-2">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-6 w-24" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    )
  }

  if (!stats) return null

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Statistics</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">Total Sales</p>
            <p className="text-xl font-bold">{formatCurrency(stats.totalSales)}</p>
          </div>

          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">Order Count</p>
            <p className="text-xl font-bold">{stats.orderCount}</p>
          </div>

          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">Avg Order</p>
            <p className="text-xl font-bold">{formatCurrency(stats.avgOrderValue)}</p>
          </div>

          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">Balance</p>
            <p className={`text-xl font-bold ${
              stats.balance > 0 ? 'text-red-600' :
              stats.balance < 0 ? 'text-green-600' :
              'text-gray-600'
            }`}>
              {formatCurrency(stats.balance)}
            </p>
          </div>
        </div>

        {stats.lastOrderDate && (
          <div className="mt-4 pt-4 border-t">
            <p className="text-xs text-muted-foreground">Last Order</p>
            <p className="text-sm font-medium">
              {new Date(stats.lastOrderDate).toLocaleDateString('en-GB', {
                weekday: 'short',
                day: 'numeric',
                month: 'short',
                year: 'numeric'
              })}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
